import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="modal"
// Drives a native <dialog>. The browser handles Escape and focus-trap; background
// scroll-lock is done in CSS (html:has(.odo-modal[open])). We open/close and add
// light-dismiss (click on the backdrop).
//
// Turbo: we close the dialog on a real navigation (a visit to a DIFFERENT url) so
// the page is left/cached clean and the back button never restores a stuck-open
// modal. A same-url visit is a Turbo morph refresh (e.g. an ActiveCable
// broadcast) — we leave the modal open and let
// data-turbo-permanent carry it through the morph, so a background update never
// dismisses an open modal.
export default class extends Controller {
	static targets = ["dialog", "frame"];

	connect() {
		document.addEventListener("turbo:before-visit", this.closeOnNavigation);
	}

	disconnect() {
		document.removeEventListener("turbo:before-visit", this.closeOnNavigation);
	}

	// Dispatches "modal:open" (bubbles to window) so content that starts out
	// hidden inside the dialog — e.g. a lazy Turnstile widget, which won't
	// execute while display:none — can defer its own setup until now.
	//
	// The optional "frame" target is for a dialog whose content depends on
	// data that can go stale while the dialog itself never re-renders (it's
	// data-turbo-permanent, and nothing else on the page touches it) — e.g. a
	// list of "campaign members not yet in this encounter", which a broadcast
	// updating the encounter's participant list never refreshes. Reloading it
	// on every open re-fetches that content fresh instead of trusting
	// whatever was true when the page first loaded.
	open() {
		if (!this.dialogTarget.open) {
			if (this.hasFrameTarget) this.reloadFrame();
			this.dialogTarget.showModal();
			this.dispatch("open");
		}
	}

	// Reloads the lazy frame's content on open. If the frame carries a
	// data-canonical-src (the URL it should always reopen at), reset src to it
	// first: a POST inside the frame — e.g. the random-encounter roller's "Rolar"
	// — leaves frame.src pointing at the action URL, which a plain reload() would
	// then re-GET and 404 on. Changing the src attribute reloads the frame; when
	// it already equals the canonical URL (or there is none), reload() forces it.
	reloadFrame() {
		const canonical = this.frameTarget.getAttribute("data-canonical-src");
		if (canonical && this.frameTarget.getAttribute("src") !== canonical) {
			this.frameTarget.setAttribute("src", canonical);
		} else {
			this.frameTarget.reload();
		}
	}

	close() {
		this.dialogTarget.close();
	}

	// A form inside the dialog submitted successfully: close and reset. The dialog is
	// data-turbo-permanent, so without this it survives the render still open and still
	// filled in — the user sees the form again instead of the flash confirming the save.
	closeOnSuccess(event) {
		if (!event.detail?.success) return;
		this.dialogTarget.close();
		this.dialogTarget.querySelector("form")?.reset();
	}

	// Close on a real navigation (a visit to a different URL); a same-URL visit is a
	// Turbo morph refresh (e.g. a broadcast) where data-turbo-permanent keeps us open.
	closeOnNavigation = (event) => {
		const url = event.detail?.url;
		if (!url || !this.dialogTarget.open) {
			return;
		}
		const path = (u) => u.split("#")[0];
		if (path(url) !== path(window.location.href)) {
			this.dialogTarget.close();
		}
	};

	// A click whose target is the <dialog> itself (the empty centering area) = backdrop.
	backdropClose(event) {
		if (event.target === this.dialogTarget) {
			this.dialogTarget.close();
		}
	}
}
